import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import PostAdd from '@mui/icons-material/PostAdd'
import { CreateCampaignContext, STEP } from '~/contexts/CreateCampaignContext'
import {
  CampaignCategory,
  CopyCampaignFragment,
  TicketModelCategory,
  useAvailableAndRevisionCampaignsQuery,
} from '~/generated/graphql'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  Container,
  Link,
  SelectChangeEvent,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { CampaignSelect, CustomFormControlLabel } from '../CreateMethods/Card'
import { pagesPath } from '~/lib/$path'
import { decodeId } from '~/lib/convertId'
import { Category } from '../Category'
import router from 'next/router'
import { ErrorAlert } from '~/components/common/ErrorAlert'
import NextLink from 'next/link'

export const CreateMethod: React.FC = () => {
  const { data, loading, error } = useAvailableAndRevisionCampaignsQuery()
  const { campaign, setCampaign, copyCampaign, resetCampaign, changePageStep } = useContext(CreateCampaignContext)
  const { category } = router.query as { category?: CampaignCategory }
  const [selectedCategory, setSelectedCategory] = useState<Exclude<CampaignCategory | null, 'EVENT'>>(
    (category === CampaignCategory.Event ? null : category) ?? campaign.category ?? null,
  )
  useEffect(() => {
    // もし、カテゴリーが指定されていない場合、チケットの有無に応じてカテゴリーを自動選択する
    // ただし、通販、店舗両方のチケットがある場合は、選択されない
    let ticketCategory: CampaignCategory | undefined = undefined
    if (data && !category) {
      const availableTickets = data.viewer.company?.availableTickets ?? []
      const hasServiceTicket =
        availableTickets.filter((ticket) => ticket.category === TicketModelCategory.Locale).length >= 1
      const hasProductTicket =
        availableTickets.filter((ticket) => ticket.category === TicketModelCategory.Remote).length >= 1
      if (hasServiceTicket && !hasProductTicket) {
        ticketCategory = CampaignCategory.Service
      } else if (!hasServiceTicket && hasProductTicket) {
        ticketCategory = CampaignCategory.Product
      }
    }
    const categoryParam = category || ticketCategory || ''
    setCampaign((campaign) => {
      const category = campaign.category ?? categoryParam
      if (!category) {
        return campaign
      }
      const newCategory = category === CampaignCategory.Event ? null : category
      setSelectedCategory(newCategory)
      return {
        ...campaign,
        category: newCategory,
      }
    })
  }, [data, category, setCampaign])

  const commit = useCallback(() => {
    // TODO: 選択されずに commit された場合はエラーを表示する
    if (!selectedCategory) return
    resetCampaign()
    setCampaign((campaign) => ({
      ...campaign,
      category: selectedCategory,
    }))
    changePageStep(STEP.NEXT)
  }, [setCampaign, resetCampaign, selectedCategory, changePageStep])

  const handleCopySelectedCampaign = useCallback(
    (campaign: CopyCampaignFragment) => {
      copyCampaign({
        ...campaign,
      })
      setSelectedCategory(campaign.category === CampaignCategory.Event ? null : campaign.category)
      setCampaign((campaign) => ({
        ...campaign,
        category: campaign.category,
      }))
      changePageStep(STEP.NEXT)
    },
    [copyCampaign, setSelectedCategory, setCampaign, changePageStep],
  )

  const handleSelect = (event: SelectChangeEvent<string>) => {
    const value = event.target.value
    if (value) {
      setSelectedCategory(value as typeof selectedCategory)
    }
  }

  const hasRevisionCampaigns = data?.viewer.revisionCampaigns && data.viewer.revisionCampaigns.length > 0

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        commit()
      }}
    >
      <Container maxWidth="sm">
        <Stack
          gap={3}
          pb={5}
          alignItems="center"
          justifyContent="center"
          sx={{
            mt: {
              xs: hasRevisionCampaigns ? 8 : 12,
              sm: hasRevisionCampaigns ? 8 : 15,
              md: hasRevisionCampaigns ? 10 : 15,
            },
          }}
        >
          {hasRevisionCampaigns && (
            <Alert severity="error" sx={{ mt: 4, width: '100%' }}>
              <Typography variant="body1" mb={1}>
                修正が必要なキャンペーンがあります
              </Typography>
              <Stack gap={0.5} width="fit-content">
                {data.viewer.revisionCampaigns.map((campaign) => (
                  <Link
                    key={campaign.id}
                    component={NextLink}
                    href={pagesPath.campaigns._campaignId(decodeId(campaign.id).id).$url()}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {campaign.name}
                  </Link>
                ))}
              </Stack>
            </Alert>
          )}
          <Card variant="outlined" sx={{ p: 5 }}>
            <Stack gap={4}>
              <Typography variant="h3" fontWeight="bold" textAlign="center">
                作成方法を選択してください
              </Typography>

              {error ? (
                <ErrorAlert
                  title="キャンペーンデータの取得に失敗しました。お手数ですが、リロードしてください。"
                  error={error}
                />
              ) : loading ? (
                <LoadingSkelton />
              ) : (
                <>
                  {data && data.viewer.availableCampaigns.length > 0 && (
                    <Box>
                      <CustomFormControlLabel
                        value="公開したキャンペーンから作成"
                        icon={<FactCheckOutlinedIcon color="primary" />}
                        tooltip={
                          <Stack gap={2}>
                            <Typography variant="body2">
                              過去に公開したキャンペーンの一部を変更して、新しく作成したい方に向けた機能です。
                            </Typography>
                            <Stack gap={1}>
                              <Typography variant="body2">
                                ※本機能は最新の形式で作成されたキャンペーンが対象です。
                              </Typography>
                              <Typography variant="body2">
                                ※以前と同じ内容をそのまま公開したい場合は、定期公開機能をご利用ください。
                              </Typography>
                            </Stack>
                          </Stack>
                        }
                      />

                      <CampaignSelect
                        campaigns={data.viewer.availableCampaigns}
                        handleCampaign={handleCopySelectedCampaign}
                        availableTickets={data.viewer.company?.availableTickets}
                      />
                    </Box>
                  )}
                </>
              )}

              <Stack gap={1.5}>
                <CustomFormControlLabel value="新しく作成" icon={<PostAdd color="primary" />} />

                <Category
                  category={selectedCategory ?? ''}
                  handleSelect={handleSelect}
                  availableTickets={data?.viewer.company?.availableTickets}
                  ticketLoading={loading}
                />
              </Stack>

              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button type="submit" variant="contained" size="large" fullWidth disabled={!selectedCategory}>
                  キャンペーン作成を始める
                </Button>
              </Box>
            </Stack>
          </Card>
        </Stack>
      </Container>
    </form>
  )
}

const LoadingSkelton = () => {
  return (
    <Skeleton
      variant="rectangular"
      sx={{
        height: 100,
      }}
    />
  )
}
